// ─── mapping/pageMap.js ───────────────────────────────────────────────────────
// Creates one pageWidget per <section>/<footer>, computes their heights.
//
// Order convention (matches app rendering — last order = first visible page):
//   sections[0]  (hero/top)    → order = N-1  (last  → renders last  in app)
//   sections[N-1](footer/last) → order = 0    (first → renders first in app)

import shortid from 'shortid';
import { PAGE } from 'constants/widgetTypes.jsx';

// ─── buildSectionPageMap ──────────────────────────────────────────────────────
/**
 * Creates one fresh PAGE widget for every <section> and <footer> element.
 * No page widgets need to be passed in — everything is generated from the DOM.
 *
 * Menu element is mapped to the first section's page widget (hero page).
 *
 * @param {Document} doc
 * @returns {{
 *   sectionPageMap:    Map<Element, { pageUid: string, pageWidget: Object }>,
 *   allNewPageWidgets: Array,
 *   firstPageUid:      string|null,
 * }}
 */
export function buildSectionPageMap(doc) {
    const sectionPageMap    = new Map();
    const allNewPageWidgets = [];

    // Collect all candidate section-level elements.
    // <header> tags are included UNLESS they carry data-widget="menu".
    // Sections narrower than 1150px are nested layout cells (e.g. bento grid
    // items) and must NOT become page widgets — they are tracked separately so
    // index.js can resolve the correct parent page uid for their children.
    const allCandidates = Array.from(doc.querySelectorAll('section, footer, header')).filter(
        (el) => el.tagName.toLowerCase() !== 'header' || el.getAttribute('data-widget') !== 'menu',
    );

    const narrowSectionEls = new Set();
    const sections = allCandidates.filter((el) => {
        if (el.getBoundingClientRect().width < 1150) {
            narrowSectionEls.add(el);
            return false;
        }
        return true;
    });

    const menuEl = doc.querySelector('[data-widget="menu"]');

    if (!sections.length)
        return { sectionPageMap, allNewPageWidgets, narrowSectionEls, firstPageUid: null };

    const total = sections.length;

    sections.forEach((el, idx) => {
        // last section in DOM → order 0 (top of app)
        // first section in DOM → order N-1 (bottom of app)
        const order  = (total - 1) - idx;
        const newUid = `widget_${shortid.generate()}`;

        const rawId  = el.getAttribute('id') || '';
        const label  = rawId
            ? rawId.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
            : `${el.tagName.charAt(0) + el.tagName.slice(1).toLowerCase()} ${idx + 1}`;

        const newPW = {
            uid:     newUid,
            type:    PAGE,
            name:    'Frame',
            content: label,
            order,
            properties: {
                desktop: {
                    autoCreateAlias: false,
                    visible:         true,
                    container:       { minWidth: 1200 },
                    layout:          { width: '1200px', height: '600px' },
                },
                mobile: {
                    autoCreateAlias: false,
                    container:       { minWidth: 375 },
                    layout:          { width: '375px', height: '600px' },
                },
            },
        };

        allNewPageWidgets.push(newPW);
        sectionPageMap.set(el, { pageUid: newUid, pageWidget: newPW });
    });

    // Menu → same page as first section (hero)
    const firstMapping = sectionPageMap.get(sections[0]) ?? null;
    const firstPageUid = firstMapping?.pageUid ?? null;

    if (menuEl && firstMapping) {
        sectionPageMap.set(menuEl, { pageUid: firstMapping.pageUid, pageWidget: firstMapping.pageWidget });
    }

    return { sectionPageMap, allNewPageWidgets, narrowSectionEls, firstPageUid };
}

// ─── applyPageWidgetHeights ───────────────────────────────────────────────────
/**
 * Writes the rendered height of each section into its pageWidget.
 * Hero page additionally gets the menu bar height.
 * Clones every nested level to avoid shared-reference mutation bugs.
 */
export function applyPageWidgetHeights(
    allPageWidgets,
    sectionPageMap,
    desktopMenuEl,
    mobileMenuEl,
    mobileHeights,
    heroPageUid,
) {
    const desktopH = {};
    const mobileH  = {};
    allPageWidgets.forEach((pw) => { desktopH[pw.uid] = 0; mobileH[pw.uid] = 0; });

    sectionPageMap.forEach(({ pageUid }, sectionEl) => {
        const tag = sectionEl.tagName?.toLowerCase();
        if (tag !== 'section' && tag !== 'footer' && tag !== 'header') return;
        if (!(pageUid in desktopH)) return;
        desktopH[pageUid] += Math.round(sectionEl.getBoundingClientRect().height);
    });

    const heroUid = heroPageUid || allPageWidgets[0]?.uid;
    if (heroUid && desktopMenuEl && heroUid in desktopH)
        desktopH[heroUid] += Math.round(desktopMenuEl.getBoundingClientRect().height);

    Object.keys(mobileHeights).forEach((uid) => {
        if (uid in mobileH) mobileH[uid] = mobileHeights[uid];
    });
    if (heroUid && mobileMenuEl && heroUid in mobileH)
        mobileH[heroUid] += Math.round(mobileMenuEl.getBoundingClientRect().height);

    allPageWidgets.forEach((pw) => {
        const dh = desktopH[pw.uid] ?? 0;
        const mh = mobileH[pw.uid] ?? 0;

        // Always write the measured height — even small sections get their real
        // height. The default '600px' in the page widget template is only a
        // placeholder; it must always be replaced with the actual rendered value
        // so short sections don't get 600px of empty space below them.
        if (pw.properties?.desktop?.layout) {
            pw.properties = {
                ...pw.properties,
                desktop: {
                    ...pw.properties.desktop,
                    layout: { ...pw.properties.desktop.layout, height: `${dh}px` },
                },
            };
        }
        if (pw.properties?.mobile?.layout) {
            pw.properties = {
                ...pw.properties,
                mobile: {
                    ...pw.properties.mobile,
                    layout: { ...pw.properties.mobile.layout, height: `${mh}px` },
                },
            };
        }
    });
}

// ─── collectMobileSectionHeights ─────────────────────────────────────────────
/**
 * Pairs mobile sections with desktop sections by DOM index, sums heights.
 */
export function collectMobileSectionHeights(mobileDoc, sectionPageMap) {
    const heights         = {};
    const mobileSections  = Array.from(mobileDoc.querySelectorAll('section, footer, header')).filter(
        (el) => el.tagName.toLowerCase() !== 'header' || el.getAttribute('data-widget') !== 'menu',
    );
    const desktopSections = [];

    sectionPageMap.forEach((_, el) => {
        const t = el.tagName?.toLowerCase();
        if (t === 'section' || t === 'footer' || t === 'header') desktopSections.push(el);
    });

    desktopSections.forEach((dEl, i) => {
        const mEl     = mobileSections[i];
        const mapping = sectionPageMap.get(dEl);
        if (!mEl || !mapping) return;
        heights[mapping.pageUid] = (heights[mapping.pageUid] || 0) + Math.round(mEl.getBoundingClientRect().height);
    });

    return heights;
}